import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { API_URL } from '../../api';

const TempleAdmin = () => {
    const { user, isAuthenticated } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [empowermentRequests, setEmpowermentRequests] = useState([]);
    const [practices, setPractices] = useState([]);
    const [deities, setDeities] = useState([]);
    const [error, setError] = useState(null);

    // Deny modal state
    const [denyModal, setDenyModal] = useState(null); // { requestId, userId, deityId } | null
    const [denyReason, setDenyReason] = useState('');
    const [actionError, setActionError] = useState(null);

    // Media upload state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadFolder, setUploadFolder] = useState('deities/');
    const [uploadSubfolder, setUploadSubfolder] = useState('');
    const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'success' | 'error'
    const [uploadError, setUploadError] = useState('');
    const [uploadedUrl, setUploadedUrl] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const fileInputRef = useRef(null);

    const authHeaders = () => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user?.idToken}`,
    });

    useEffect(() => {
        if (!isAuthenticated || !user?.isAdmin) return;

        const loadData = async () => {
            setError(null);
            try {
                // Load empowerment requests from real API (all statuses)
                const [pendingRes, approvedRes] = await Promise.all([
                    fetch(`${API_URL}/temple/empowerments?status=PENDING`, { headers: authHeaders() }),
                    fetch(`${API_URL}/temple/empowerments?status=APPROVED`, { headers: authHeaders() }),
                ]);

                const pendingData = pendingRes.ok ? await pendingRes.json() : { requests: [] };
                const approvedData = approvedRes.ok ? await approvedRes.json() : { requests: [] };
                setEmpowermentRequests([
                    ...(pendingData.requests || []),
                    ...(approvedData.requests || []),
                ]);

                // Load deities
                const deitiesRes = await fetch(`${API_URL}/temple/deities`, { headers: authHeaders() });
                if (deitiesRes.ok) {
                    const deitiesData = await deitiesRes.json();
                    setDeities(deitiesData.deities || []);
                }

                // Load practices
                const practicesRes = await fetch(`${API_URL}/temple/practices`, { headers: authHeaders() });
                if (practicesRes.ok) {
                    const practicesData = await practicesRes.json();
                    setPractices(practicesData.practices || []);
                }
            } catch (err) {
                console.error('Error loading admin data:', err);
                setError('Failed to load admin data. Please refresh.');
            }
        };

        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?.isAdmin, user?.idToken]);

    // Check if user is admin
    if (!isAuthenticated || !user?.isAdmin) {
        return <Navigate to="/temple" replace />;
    }

    // ── Empowerment actions ───────────────────────────────────────────────────

    const reviewEmpowerment = async (userId, deityId, status, reason) => {
        const previousRequests = empowermentRequests;

        // Optimistic update
        setEmpowermentRequests(prev =>
            prev.map(req =>
                req.userId === userId && req.deityId === deityId
                    ? { ...req, status, reviewedAt: new Date().toISOString().split('T')[0] }
                    : req
            )
        );
        setActionError(null);

        try {
            // Backend lambda expects an `action` enum (APPROVE | DENY), not a status string.
            const action = status === 'approved' ? 'APPROVE' : 'DENY';
            const res = await fetch(`${API_URL}/temple/empowerments`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({
                    userId,
                    deityId,
                    action,
                    reviewerId: user.userId,
                    reviewerName: user.displayName || user.email,
                    notes: reason || '',
                }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || `API error: ${res.status}`);
            }
        } catch (err) {
            console.error('Error reviewing empowerment:', err);
            // Revert optimistic update
            setEmpowermentRequests(previousRequests);
            setActionError(`Failed to update request: ${err.message}`);
        }
    };

    const handleApproveRequest = (req) => {
        reviewEmpowerment(req.userId, req.deityId, 'approved', '');
    };

    const handleDenySubmit = async () => {
        if (!denyModal) return;
        await reviewEmpowerment(denyModal.userId, denyModal.deityId, 'denied', denyReason);
        setDenyModal(null);
        setDenyReason('');
    };

    // ── Deity / Practice toggles ──────────────────────────────────────────────

    const toggleDeityStatus = async (deityId) => {
        const previousDeities = deities;
        const deity = deities.find(d => d.id === deityId);
        if (!deity) return;

        const newStatus = deity.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

        // Optimistic update
        setDeities(prev =>
            prev.map(d => d.id === deityId ? { ...d, status: newStatus } : d)
        );
        setActionError(null);

        try {
            const res = await fetch(`${API_URL}/admin/deities/${deityId}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status: newStatus }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || `API error: ${res.status}`);
            }
        } catch (err) {
            console.error('Error toggling deity status:', err);
            setDeities(previousDeities);
            setActionError(`Failed to update deity status: ${err.message}`);
        }
    };

    const togglePracticeStatus = async (practiceId) => {
        const previousPractices = practices;
        const practice = practices.find(p => p.id === practiceId);
        if (!practice) return;

        const newStatus = practice.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';

        // Optimistic update
        setPractices(prev =>
            prev.map(p => p.id === practiceId ? { ...p, status: newStatus } : p)
        );
        setActionError(null);

        try {
            const res = await fetch(`${API_URL}/admin/practices/${practiceId}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status: newStatus }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || `API error: ${res.status}`);
            }
        } catch (err) {
            console.error('Error toggling practice status:', err);
            setPractices(previousPractices);
            setActionError(`Failed to update practice status: ${err.message}`);
        }
    };

    // ── Media upload ──────────────────────────────────────────────────────────

    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        if (!uploadFile) return;

        setUploadStatus('uploading');
        setUploadError('');
        setUploadedUrl('');
        setCopySuccess(false);

        try {
            // Step 1: get the pre-signed PUT URL from our API
            const urlRes = await fetch(`${API_URL}/admin/upload-url`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    filename: uploadFile.name,
                    contentType: uploadFile.type || 'application/octet-stream',
                    folder: uploadFolder,
                    ...(uploadSubfolder.trim() ? { subfolder: uploadSubfolder.trim() } : {}),
                }),
            });

            if (!urlRes.ok) {
                const data = await urlRes.json().catch(() => ({}));
                throw new Error(data.message || `Failed to get upload URL: ${urlRes.status}`);
            }

            const { uploadUrl, assetUrl } = await urlRes.json();

            // Step 2: PUT the file directly to S3 using the pre-signed URL
            const s3Res = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': uploadFile.type || 'application/octet-stream',
                },
                body: uploadFile,
            });

            if (!s3Res.ok) {
                throw new Error(`S3 upload failed: ${s3Res.status}`);
            }

            setUploadedUrl(assetUrl);
            setUploadStatus('success');
            // Reset file input
            setUploadFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err) {
            console.error('Upload error:', err);
            setUploadStatus('error');
            setUploadError(err.message || 'Upload failed. Please try again.');
        }
    };

    const handleCopyUrl = () => {
        if (!uploadedUrl) return;
        navigator.clipboard.writeText(uploadedUrl).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    const formatDuration = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} min`;
    };

    const getStatusColor = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'PENDING': return 'text-yellow-400 bg-yellow-900 bg-opacity-20';
            case 'APPROVED': return 'text-green-400 bg-green-900 bg-opacity-20';
            case 'DENIED': return 'text-red-400 bg-red-900 bg-opacity-20';
            default: return 'text-gray-400 bg-gray-900 bg-opacity-20';
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'empowerments', label: 'Empowerment Requests' },
        { id: 'deities', label: 'Deities' },
        { id: 'practices', label: 'Practices' },
        { id: 'media', label: 'Media Upload' },
        { id: 'admins', label: 'Admin Management' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white">
            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">Temple Administration</h1>
                    <p className="text-gray-300">Manage empowerments, practices, and sacred content</p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-900 bg-opacity-30 border border-red-600 rounded-lg text-red-300">
                        {error}
                    </div>
                )}

                {actionError && (
                    <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-600 rounded-lg text-red-300 text-sm">
                        {actionError}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex flex-wrap gap-1 bg-gray-800 bg-opacity-50 rounded-lg p-1 mb-8">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors text-sm whitespace-nowrap ${
                                activeTab === tab.id
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                                <h3 className="text-lg font-semibold mb-2">Pending Requests</h3>
                                <div className="text-3xl font-bold text-yellow-400">
                                    {empowermentRequests.filter(req => (req.status || '').toUpperCase() === 'PENDING').length}
                                </div>
                            </div>
                            <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                                <h3 className="text-lg font-semibold mb-2">Active Practices</h3>
                                <div className="text-3xl font-bold text-green-400">
                                    {practices.filter(p => p.status === 'PUBLISHED').length}
                                </div>
                            </div>
                            <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                                <h3 className="text-lg font-semibold mb-2">Protected Practices</h3>
                                <div className="text-3xl font-bold text-amber-400">
                                    {practices.filter(p => p.isProtected).length}
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                            <h3 className="text-lg font-semibold mb-4">Recent Requests</h3>
                            {empowermentRequests.length === 0 ? (
                                <p className="text-gray-400">No empowerment requests yet.</p>
                            ) : (
                                <div className="space-y-2">
                                    {empowermentRequests.slice(0, 5).map((req, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm">
                                            <span className="text-gray-300">
                                                {req.userName || req.userId} — {req.deityName || req.deityId}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(req.status)}`}>
                                                {req.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Empowerment Requests Tab */}
                {activeTab === 'empowerments' && (
                    <div>
                        <h2 className="text-2xl font-bold mb-6">Empowerment Requests</h2>
                        {empowermentRequests.length === 0 ? (
                            <p className="text-gray-400">No empowerment requests found.</p>
                        ) : (
                            <div className="space-y-4">
                                {empowermentRequests.map((request, idx) => (
                                    <div key={idx} className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-xl font-semibold">{request.userName || request.userId}</h3>
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                                {(request.status || '').toUpperCase()}
                                            </span>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <p className="text-gray-400 text-sm">Email</p>
                                                <p className="text-white">{request.userEmail || '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-400 text-sm">Practice</p>
                                                <p className="text-white">{request.deityName || request.deityId}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-400 text-sm">Lineage</p>
                                                <p className="text-white">{request.lineageKey || '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-400 text-sm">Submitted</p>
                                                <p className="text-white">
                                                    {request.createdAt
                                                        ? new Date(request.createdAt).toLocaleDateString()
                                                        : '—'}
                                                </p>
                                            </div>
                                        </div>

                                        {request.teacherReference && (
                                            <div className="mb-4">
                                                <p className="text-gray-400 text-sm">Teacher Reference</p>
                                                <p className="text-white">{request.teacherReference}</p>
                                            </div>
                                        )}

                                        {(request.notes || request.reason) && (
                                            <div className="mb-4">
                                                <p className="text-gray-400 text-sm">Notes</p>
                                                <p className="text-white">{request.notes || request.reason}</p>
                                            </div>
                                        )}

                                        {(request.status || '').toUpperCase() === 'PENDING' && (
                                            <div className="flex gap-4">
                                                <button
                                                    onClick={() => handleApproveRequest(request)}
                                                    className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setDenyModal({ userId: request.userId, deityId: request.deityId });
                                                        setDenyReason('');
                                                    }}
                                                    className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                                                >
                                                    Deny
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Deities Management Tab */}
                {activeTab === 'deities' && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold">Deities Management</h2>
                        </div>

                        <div className="mb-6 p-4 bg-gray-800 bg-opacity-40 border border-gray-700 rounded-lg text-sm text-gray-400">
                            Deity creation is coming. For now, the temple's deities are managed
                            directly by the site custodians.
                        </div>

                        <div className="space-y-4">
                            {deities.map((deity) => (
                                <div key={deity.id} className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-semibold">{deity.name}</h3>
                                        <div className="flex items-center space-x-2">
                                            {deity.requiresEmpowerment && (
                                                <span className="bg-amber-600 bg-opacity-20 text-amber-300 px-2 py-1 rounded-full text-xs font-medium">
                                                    Empowerment Required
                                                </span>
                                            )}
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                deity.status === 'ACTIVE'
                                                    ? 'bg-green-600 bg-opacity-20 text-green-300'
                                                    : 'bg-red-600 bg-opacity-20 text-red-300'
                                            }`}>
                                                {deity.status}
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-gray-300 mb-4">{deity.description}</p>

                                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <p className="text-gray-400 text-sm">ID</p>
                                            <p className="text-white font-mono text-sm">{deity.id}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-400 text-sm">Lineage</p>
                                            <p className="text-white">{deity.lineageKey || 'Open'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-400 text-sm">Created</p>
                                            <p className="text-white">{deity.createdAt}</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 items-center">
                                        <button
                                            onClick={() => toggleDeityStatus(deity.id)}
                                            className={`px-4 py-2 rounded-lg font-semibold transition-colors text-sm ${
                                                deity.status === 'ACTIVE'
                                                    ? 'bg-red-600 hover:bg-red-700'
                                                    : 'bg-green-600 hover:bg-green-700'
                                            }`}
                                        >
                                            {deity.status === 'ACTIVE' ? 'Take Offline' : 'Bring Online'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Practice Management Tab */}
                {activeTab === 'practices' && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold">Practices Management</h2>
                        </div>

                        <div className="mb-6 p-4 bg-gray-800 bg-opacity-40 border border-gray-700 rounded-lg text-sm text-gray-400">
                            Practice creation is coming. For now, practices are managed
                            directly by the site custodians.
                        </div>

                        <div className="space-y-4">
                            {practices.map((practice) => (
                                <div key={practice.id} className="bg-gray-800 bg-opacity-50 rounded-lg p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-semibold">{practice.title}</h3>
                                        <div className="flex items-center space-x-2">
                                            {practice.isProtected && (
                                                <span className="bg-amber-600 bg-opacity-20 text-amber-300 px-2 py-1 rounded-full text-xs font-medium">
                                                    Protected
                                                </span>
                                            )}
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                practice.mood === 'peaceful'
                                                    ? 'bg-blue-600 bg-opacity-20 text-blue-300'
                                                    : 'bg-red-600 bg-opacity-20 text-red-300'
                                            }`}>
                                                {practice.mood}
                                            </span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                practice.status === 'PUBLISHED'
                                                    ? 'bg-green-600 bg-opacity-20 text-green-300'
                                                    : 'bg-yellow-600 bg-opacity-20 text-yellow-300'
                                            }`}>
                                                {practice.status}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                                        <div>
                                            <p className="text-gray-400 text-sm">Duration</p>
                                            <p className="text-white">{formatDuration(practice.duration)}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-400 text-sm">Deity</p>
                                            <p className="text-white capitalize">{practice.deityId}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-400 text-sm">Created</p>
                                            <p className="text-white">{practice.createdAt}</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 items-center">
                                        <button
                                            onClick={() => togglePracticeStatus(practice.id)}
                                            className={`px-4 py-2 rounded-lg font-semibold transition-colors text-sm ${
                                                practice.status === 'PUBLISHED'
                                                    ? 'bg-yellow-600 hover:bg-yellow-700'
                                                    : 'bg-green-600 hover:bg-green-700'
                                            }`}
                                        >
                                            {practice.status === 'PUBLISHED' ? 'Set to Draft' : 'Publish Live'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Media Upload Tab */}
                {activeTab === 'media' && (
                    <div>
                        <h2 className="text-2xl font-bold mb-6">Media Upload</h2>

                        <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6 max-w-2xl">
                            <p className="text-gray-300 mb-6 text-sm">
                                Upload images, audio, video, and other assets to the CDN. Files are served from
                                <span className="font-mono text-blue-300 ml-1">assets.buddhawisdom.org</span> and
                                video URLs can be played directly in a player or embedded on a page.
                            </p>

                            <form onSubmit={handleUploadSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        File
                                    </label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        required
                                        onChange={(e) => {
                                            setUploadFile(e.target.files[0] || null);
                                            setUploadStatus(null);
                                            setUploadedUrl('');
                                        }}
                                        className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                                    />
                                    {uploadFile && (
                                        <p className="mt-1 text-xs text-gray-400">
                                            {uploadFile.name} ({uploadFile.size >= 1024 * 1024
                                                ? `${(uploadFile.size / (1024 * 1024)).toFixed(1)} MB`
                                                : `${(uploadFile.size / 1024).toFixed(1)} KB`})
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Folder
                                    </label>
                                    <select
                                        value={uploadFolder}
                                        onChange={(e) => setUploadFolder(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="deities/">deities/ — deity images</option>
                                        <option value="practices/">practices/ — practice images/media</option>
                                        <option value="audio/">audio/ — audio stems</option>
                                        <option value="videos/">videos/ — video teachings & clips</option>
                                        <option value="monks/">monks/ — photographs of monks & teachers</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Subfolder <span className="text-gray-500 font-normal">(optional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={uploadSubfolder}
                                        onChange={(e) => setUploadSubfolder(e.target.value)}
                                        placeholder="e.g. tara-visualization"
                                        className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="mt-1 text-xs text-gray-400">
                                        Lowercase letters, digits, dashes. Practice audio must use the practice id
                                        as the subfolder (e.g. audio/ + tara-visualization + ambient-bed.m4a).
                                    </p>
                                </div>

                                {uploadStatus === 'error' && (
                                    <div className="p-3 bg-red-900 bg-opacity-30 border border-red-600 rounded-lg text-red-300 text-sm">
                                        {uploadError}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={!uploadFile || uploadStatus === 'uploading'}
                                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg font-semibold transition-colors"
                                >
                                    {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload to CDN'}
                                </button>
                            </form>

                            {/* Success state */}
                            {uploadStatus === 'success' && uploadedUrl && (
                                <div className="mt-6 p-4 bg-green-900 bg-opacity-30 border border-green-600 rounded-lg">
                                    <p className="text-green-300 font-semibold mb-2">Upload successful</p>
                                    <p className="text-sm text-gray-300 mb-3">CDN asset URL:</p>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-xs bg-gray-900 px-3 py-2 rounded text-blue-300 break-all">
                                            {uploadedUrl}
                                        </code>
                                        <button
                                            onClick={handleCopyUrl}
                                            className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                                copySuccess
                                                    ? 'bg-green-700 text-white'
                                                    : 'bg-gray-600 hover:bg-gray-500 text-white'
                                            }`}
                                        >
                                            {copySuccess ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Admin Management Tab */}
                {activeTab === 'admins' && (
                    <div>
                        <h2 className="text-2xl font-bold mb-6">Admin Management</h2>

                        <div className="bg-gray-800 bg-opacity-50 rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-semibold mb-4">Current Administrators</h3>
                            <div className="space-y-3">
                                {['logan@buddhawisdom.org', 'brandon@buddhawisdom.org'].map((email) => (
                                    <div key={email} className="flex items-center justify-between p-3 bg-gray-700 bg-opacity-50 rounded-lg">
                                        <div>
                                            <p className="text-white font-semibold">{email}</p>
                                            <p className="text-gray-400 text-sm">Administrator — full access</p>
                                        </div>
                                        <span className="bg-green-600 bg-opacity-20 text-green-300 px-2 py-1 rounded-full text-xs font-medium">
                                            Active
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-4 bg-gray-800 bg-opacity-40 border border-gray-700 rounded-lg text-sm text-gray-400">
                            Administrator access is configured at the infrastructure level
                            (the <span className="font-mono text-gray-300">ADMIN_EMAILS</span> allow-list).
                            Self-service administrator management from this panel will come later.
                        </div>
                    </div>
                )}
            </div>

            {/* Deny Modal */}
            {denyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-4">Deny Empowerment Request</h3>
                        <p className="text-gray-300 text-sm mb-4">
                            Provide a brief reason for denial (optional but encouraged):
                        </p>
                        <textarea
                            className="w-full px-3 py-2 bg-gray-700 rounded-lg text-white text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
                            rows={3}
                            placeholder="e.g. We could not verify the empowerment lineage. Please contact us directly."
                            value={denyReason}
                            onChange={(e) => setDenyReason(e.target.value)}
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={handleDenySubmit}
                                className="flex-1 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                            >
                                Confirm Deny
                            </button>
                            <button
                                onClick={() => { setDenyModal(null); setDenyReason(''); }}
                                className="flex-1 bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TempleAdmin;
